import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Spinner } from '@wartek-id/spinner'
import { Shimmer } from '@wartek-id/shimmer'
import { Icon } from '@wartek-id/icon-v2'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Button } from '@wartek-id/button-v1'
import { Banner, BannerAction, BannerDescription } from '@wartek-id/banner'
import { Badge } from '@wartek-id/badge'

import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import {
  useAPIGetListGuruStates,
  useAPIGetPenetapanPredikatKinerjaStep,
  useAPIGetPraktikPembelajaranSummary,
  useAPIGetSatuanPendidikanDocuments,
  useAPIGetSupportingDocument,
} from 'api/kinerja/useAPIKinerja'
import CircularProgress, {
  CircularLock,
} from 'app/KinerjaPage/components/CircularProgress'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import PanelInfo from 'app/KinerjaPage/pages/PelaksanaanV2/components/PanelInfo'
import { REQUIRED_SUPPORTING_DOCUMENT_TYPES } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/supportingDocuments'
import { SATUAN_PENDIDIKAN_DOCUMENTS } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/satuanPendidikanDocuments'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import { PENETAPAN_PREDIKAT_KINERJA_STEPS } from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { getSatuanPendidikanByNpsn } from 'app/KinerjaShared/utils/profile'
import { assetUrl } from 'utils/url'
import { getUserDisplayName } from 'utils/user'
import DialogCloseDemoMode from 'app/KinerjaPage/components/DialogCloseDemoMode'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'
import { useNewDocumentFlow } from 'app/KinerjaShared/hooks/useNewDocumentFlow'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'
import { usePenetapanPredikatKinerjaV2Enabled } from 'app/KinerjaPage/pages/PelaksanaanV2/hooks/usePenetapanPredikatKinerjaV2Enabled'

import type { IPraktikPembelajaranSummaryItem } from 'types/kinerja-pelaksanaan'

type PenilaianKinerjaProps = {
  npsn: string
}

export function PenilaianKinerja({ npsn }: PenilaianKinerjaProps) {
  const router = useRouter()
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const { tipe } = useKinerjaTipe()

  const { status: documentFlowStatus } = useNewDocumentFlow()

  const { isDemoMode } = useDemoContext()
  const [isDialogConfirmCloseDemoMode, setDialogConfirmCloseDemoMode] =
    useState(false)

  const requiredDocumentsLength = useMemo(() => {
    return documentFlowStatus === 'NEW_DOCUMENT_FLOW'
      ? SATUAN_PENDIDIKAN_DOCUMENTS.length
      : REQUIRED_SUPPORTING_DOCUMENT_TYPES.length
  }, [documentFlowStatus])

  const isPenetapanPredikatKinerjaEnabled =
    process.env.NEXT_PUBLIC_SKP_FORM_F_ENABLED === 'true'

  const isPriorTo2025 = useIsPrior2025()
  const { isEnabled: isPenetapanPredikatKinerjaV2Enabled } =
    usePenetapanPredikatKinerjaV2Enabled({ npsn })

  const {
    profile,
    isLoading: isLoadingProfile,
    isKepalaDinas,
    profilePs,
  } = useRoleContext()

  useEffect(() => {
    trackEvent({
      activity_type: 'penilaian_ks_open_hub',
      screen_name: 'ks_penilaian_hub',
      event_trigger: 'ks_penilaian_hub',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    isFetching: isLoadingSupportingDocument,
    data: supportingDocumentData,
  } = useAPIGetSupportingDocument({
    params: {
      npsn,
    },
    options: {
      // Only for the old document flow
      enabled: documentFlowStatus !== 'NEW_DOCUMENT_FLOW',
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
    isDemoMode,
  })

  const {
    data: satuanPendidikanDocumentsData,
    isLoading: isLoadingSatuanPendidikanDocuments,
  } = useAPIGetSatuanPendidikanDocuments({
    params: { npsn },
    options: {
      // Only for the new document flow
      enabled: documentFlowStatus === 'NEW_DOCUMENT_FLOW',
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: false,
    },
  })

  const { data: listGuruStatesData, isFetching: isLoadingListGuruStates } =
    useAPIGetListGuruStates({
      npsn,
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      isDemoMode,
    })

  const {
    data: praktikPembelajaranSummaryData,
    isLoading: isLoadingPraktikPembelajaranSummary,
    isSuccess: isSuccessPraktikPembelajaranSummary,
  } = useAPIGetPraktikPembelajaranSummary({
    npsn,
    params: {
      tipe,
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: false,
    },
  })

  const { data: dataPenetapanStep, isLoading: isLoadingPenetapanStep } =
    useAPIGetPenetapanPredikatKinerjaStep({
      params: {
        npsn,
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        enabled: isPenetapanPredikatKinerjaEnabled,
      },
      isDemoMode,
    })

  const supportingDocumentsCount = useMemo(() => {
    // New document flow (>= 2025)
    if (documentFlowStatus === 'NEW_DOCUMENT_FLOW') {
      // The value is either 0 or 5
      return !!satuanPendidikanDocumentsData?.data?.flagTnc
        ? SATUAN_PENDIDIKAN_DOCUMENTS.length
        : 0
    }

    // Old document flow (2024)
    if (supportingDocumentData) {
      return REQUIRED_SUPPORTING_DOCUMENT_TYPES.reduce((acc, curr) => {
        if (
          supportingDocumentData?.data?.documents?.some(
            (document) => document.type === curr.type
          )
        ) {
          acc += 1
        }

        return acc
      }, 0)
    }

    return 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    supportingDocumentData,
    satuanPendidikanDocumentsData,
    documentFlowStatus,
  ])

  const isDocumentCompleted =
    supportingDocumentsCount >= requiredDocumentsLength

  const RenderCircularSubmission = () => {
    if (!dataPenetapanStep) return <CircularLock />

    if (isPriorTo2025) {
      switch (dataPenetapanStep?.data?.submissionStep) {
        case 'NOT_CREATED':
          return <CircularLock />
        case 'SUBMIT_KADIS':
          return <CircularProgress value={0} maxValue={2} />
        case 'SUBMIT_GURU':
          return <CircularProgress value={1} maxValue={2} />
        case 'SUBMIT_BKN':
          return <CircularProgress value={2} maxValue={2} />
        default:
          return <Spinner size="lg" />
      }
    }

    return (
      <CircularProgress
        value={dataPenetapanStep?.data?.totalCompleted || 0}
        maxValue={dataPenetapanStep?.data?.totalGuru || 0}
      />
    )
  }

  const currentSubmissionStep =
    dataPenetapanStep?.data?.submissionStep ?? 'NOT_CREATED'
  const currentSubmissionStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS[currentSubmissionStep]?.order ?? 0
  const submitGuruStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS['SUBMIT_GURU'].order
  const submitKadisStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS['SUBMIT_KADIS'].order
  const isPredikatSubmittedToGuru =
    currentSubmissionStepOrder > submitGuruStepOrder
  const isWaitingForPKO = currentSubmissionStepOrder === submitKadisStepOrder

  const hasPenilaianSusulan =
    isPredikatSubmittedToGuru &&
    listGuruStatesData?.meta?.total > 0 &&
    listGuruStatesData?.meta?.completed !== listGuruStatesData?.meta?.total

  const countPenetapanPredikatPegawaiSusulan =
    dataPenetapanStep?.data?.totalNotCompletedRotasiMutasi ?? 0
  const hasPenetapanPredikatPegawaiSusulan =
    isPredikatSubmittedToGuru && countPenetapanPredikatPegawaiSusulan > 0

  const isPredikatTotallySubmitted =
    isPredikatSubmittedToGuru &&
    listGuruStatesData?.meta?.completed === listGuruStatesData?.meta?.total &&
    countPenetapanPredikatPegawaiSusulan === 0

  const isNotSubmitted = currentSubmissionStep === 'NOT_CREATED'
  const isSubmissionReady =
    listGuruStatesData?.meta?.completed === listGuruStatesData?.meta?.total

  const isCekPenetapanDisabled = useMemo(() => {
    const isCekPenetapanDisabledBefore2025 =
      currentSubmissionStep === 'NOT_CREATED'
    const isCekPenetapanDisabledFor2025Onwards =
      !isPenetapanPredikatKinerjaV2Enabled

    return isPriorTo2025
      ? isCekPenetapanDisabledBefore2025
      : isCekPenetapanDisabledFor2025Onwards
  }, [
    isPriorTo2025,
    currentSubmissionStep,
    isPenetapanPredikatKinerjaV2Enabled,
  ])

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto">
        <Header
          title={
            !isPriorTo2025
              ? 'Penilaian Kinerja dan Penetapan Predikat Kinerja'
              : 'Penilaian Kinerja'
          }
          className="mb-9 print:hidden"
          rightNode={
            <PanelInfo
              isLoading={isLoadingProfile}
              value={getSatuanPendidikanByNpsn(profile, npsn, isDemoMode)}
            />
          }
          onBack={() => {
            if (isDemoMode) {
              setDialogConfirmCloseDemoMode(true)
              return
            }

            router.back()
          }}
        />

        {isPriorTo2025 && hasPenilaianSusulan && (
          <BannerPenilaianPenetapanSusulan className="mb-6" />
        )}

        {isPriorTo2025 && hasPenetapanPredikatPegawaiSusulan && (
          <BannerPenetapanPredikatPegawaiSusulan className="mb-6" npsn={npsn} />
        )}

        {isPredikatTotallySubmitted && (
          <BannerEvaluationSent isYear2025OrLater={!isPriorTo2025} />
        )}

        {!isPriorTo2025 && isWaitingForPKO && <BannerWaitingForPKO />}

        {isDemoMode && (
          <DialogCloseDemoMode
            isOpen={isDialogConfirmCloseDemoMode}
            onClose={() => {
              setDialogConfirmCloseDemoMode(false)
            }}
            onConfirm={() => {
              router.back()
            }}
          />
        )}

        <section className="mb-9">
          <header className="text-white space-y-1 p-4 bg-gradient-to-r from-cyan-80 to-cyan-40 rounded-t-lg">
            <Text
              className="overflow-wrap-anywhere line-clamp-1"
              color="inherit"
              variant="heading-lg"
            >
              {isLoadingProfile
                ? 'Selamat datang!'
                : `Selamat datang, ${getUserDisplayName(
                    isKepalaDinas ? profilePs?.nama : profile?.nama
                  )}!`}
            </Text>
            <Text color="inherit">
              Penilaian yang Anda lakukan merupakan aspek penting dalam
              peningkatan kinerja satuan pendidikan Anda.
            </Text>
          </header>

          <div className="bg-white rounded-b-lg px-8 py-6">
            <div className="space-y-6">
              <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
                <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                  {isLoadingListGuruStates ? (
                    <Spinner size="lg" />
                  ) : (
                    <CircularProgress
                      value={listGuruStatesData?.meta?.completed || 0}
                      maxValue={listGuruStatesData?.meta?.total || 0}
                    />
                  )}
                </div>
                <div className="space-y-2 w-full">
                  <Text variant="heading-md">Penilaian Kinerja</Text>
                  <Text color="subdued">
                    Cek Praktik Kinerja, Perilaku Kerja, dan lainnya.
                  </Text>
                </div>
                <Link
                  href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru`}
                  passHref
                >
                  <Button
                    as="a"
                    color="black"
                    className="flex-shrink-0 w-40"
                    onClick={() => {
                      trackEvent({
                        activity_type: 'penilaian_ks_hub_click_cek_daftar_guru',
                        screen_name: 'ks_penilaian_hub',
                        event_trigger: 'ks_penilaian_hub',
                      })
                    }}
                  >
                    {isSubmissionReady ? 'Kirim Data' : 'Lakukan'}
                    <span className="sr-only">Penilaian Kinerja</span>
                  </Button>
                </Link>
              </div>

              <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
                <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                  {isLoadingSupportingDocument ||
                  isLoadingSatuanPendidikanDocuments ? (
                    <Spinner size="lg" />
                  ) : (
                    <CircularProgress
                      value={supportingDocumentsCount}
                      maxValue={requiredDocumentsLength}
                    />
                  )}
                </div>
                <div className="space-y-2 w-full">
                  <Text variant="heading-md">
                    {documentFlowStatus === 'OLD_DOCUMENT_FLOW'
                      ? 'Kumpulkan dokumen'
                      : 'Konfirmasi Ketersediaan Dokumen Satuan Pendidikan'}
                  </Text>
                  <Text color="subdued">
                    {documentFlowStatus === 'OLD_DOCUMENT_FLOW'
                      ? 'Unggah dokumen yang berkaitan dengan satuan pendidikan Anda.'
                      : 'Konfirmasi ketersediaan dokumen yang berkaitan dengan satuan pendidikan Anda.'}
                  </Text>
                </div>
                <Link
                  href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/${
                    documentFlowStatus === 'OLD_DOCUMENT_FLOW'
                      ? 'kumpulkan-dokumen'
                      : 'konfirmasi-dokumen'
                  }`}
                  passHref
                >
                  <Button
                    as="a"
                    color="white"
                    className="flex-shrink-0 w-40"
                    isLoading={isLoadingSupportingDocument}
                    disabled={
                      isLoadingSupportingDocument ||
                      documentFlowStatus === 'DISABLED'
                    }
                    onClick={() => {
                      trackEvent({
                        activity_type: isDocumentCompleted
                          ? 'penilaian_ks_hub_click_cek'
                          : 'penilaian_ks_hub_click_kumpulkan',
                        screen_name: 'ks_penilaian_hub',
                        event_trigger: 'ks_penilaian_hub',
                      })
                    }}
                  >
                    {documentFlowStatus === 'OLD_DOCUMENT_FLOW' ? (
                      <>
                        {isDocumentCompleted ? 'Cek' : 'Kumpulkan'}
                        <span className="sr-only">dokumen</span>
                      </>
                    ) : documentFlowStatus === 'NEW_DOCUMENT_FLOW' ? (
                      <>
                        {isDocumentCompleted ? (
                          <>
                            Cek <span className="sr-only">dokumen</span>
                          </>
                        ) : (
                          'Lakukan'
                        )}
                      </>
                    ) : (
                      <>
                        <span className="sr-only">Pengumpulan dokumen</span>
                        Sedang diperbarui
                      </>
                    )}
                  </Button>
                </Link>
              </div>
              {isPenetapanPredikatKinerjaEnabled && (
                <>
                  <div className="flex justify-center">
                    <Icon>arrow_downward</Icon>
                  </div>

                  <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
                    <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                      <RenderCircularSubmission />
                    </div>
                    <div className="space-y-2 w-full">
                      <Text variant="heading-md">
                        Penetapan Predikat Kinerja
                      </Text>
                      {isCekPenetapanDisabled ? (
                        <>
                          {isPriorTo2025 ? (
                            <>
                              <Text color="subdued">
                                Penetapan yang Anda lakukan bersifat{' '}
                                <strong>rekomendasi</strong> dan akan dikirim ke
                                Dinas Pendidikan.
                              </Text>
                              <Text
                                variant="body-sm"
                                color="disabled"
                                className="bg-surface-neutral-subdued inline-block p-2 rounded"
                              >
                                Selesaikan Penilaian Kinerja terlebih dahulu
                              </Text>
                            </>
                          ) : isNotSubmitted && isSubmissionReady ? (
                            <Text color="subdued">
                              Kirim data Penilaian Kinerja ke Dinas Pendidikan
                              terlebih dahulu. Tekan tombol{' '}
                              <strong>Kirim Data</strong> di atas.
                            </Text>
                          ) : (
                            <Text color="subdued">
                              Penetapan yang Anda lakukan akan dikirim ke Guru.
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text color="subdued">
                          Penetapan yang Anda lakukan akan dikirim ke Guru.
                        </Text>
                      )}
                    </div>
                    <Link
                      href={
                        isPriorTo2025
                          ? `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/langkah-penetapan-predikat-kinerja`
                          : `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru/penetapan-predikat-kinerja`
                      }
                      passHref
                    >
                      <Button
                        as="a"
                        className="flex-shrink-0 w-40"
                        type={undefined}
                        onClick={() =>
                          trackEvent({
                            activity_type:
                              'penilaian_ks_hub_click_cek_penetapan',
                            screen_name: 'ks_penilaian_hub',
                            event_trigger: 'ks_penilaian_home',
                          })
                        }
                        isLoading={isLoadingPenetapanStep}
                        disabled={isCekPenetapanDisabled}
                      >
                        {!isPriorTo2025 && isPredikatTotallySubmitted
                          ? 'Cek'
                          : 'Lakukan'}
                        <span className="sr-only">
                          Penetapan Predikat Kinerja
                        </span>
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="rounded bg-white p-6 space-y-6">
          <header className="flex space-x-6 items-center">
            <div className="space-y-1 w-full">
              <Text variant="heading-md">
                Cek dimensi Rapor Pendidikan pilihan Guru
              </Text>
              <Text color="subdued">
                Berikut yang menjadi fokus peningkatan kinerja Guru di satuan
                pendidikan Anda.
              </Text>
            </div>
            <Button
              as="a"
              target="_blank"
              color="white"
              className="flex-shrink-0"
              href="https://raporpendidikan.kemendikdasmen.go.id/"
              onClick={() => {
                trackEvent({
                  activity_type: 'penilaian_ks_hub_click_selengkapnya',
                  screen_name: 'ks_penilaian_hub',
                  event_trigger: 'ks_penilaian_hub',
                })
              }}
            >
              Selengkapnya <Icon>chevron_right</Icon>
              <span className="sr-only">
                dimensi Rapor Pendidikan pilihan Guru
              </span>
            </Button>
          </header>

          {isLoadingPraktikPembelajaranSummary && (
            <Shimmer height="160px" width="100%" />
          )}

          {!isLoadingPraktikPembelajaranSummary &&
            isSuccessPraktikPembelajaranSummary && (
              <PraktikPembelajaranSummaries
                items={
                  praktikPembelajaranSummaryData?.data?.praktikPembelajaran ??
                  []
                }
              />
            )}
        </section>
      </div>
    </div>
  )
}

type PraktikPembelajaranSummariesProps = {
  items: Array<IPraktikPembelajaranSummaryItem>
}
function PraktikPembelajaranSummaries({
  items,
}: PraktikPembelajaranSummariesProps) {
  if (items.length === 0) {
    return null
  }

  const renderBadge = (score: number) => {
    if (score >= 3) {
      return <Badge variant="success">Baik</Badge>
    } else if (score >= 2) {
      return <Badge variant="warning">Cukup</Badge>
    } else {
      return <Badge variant="critical">Kurang</Badge>
    }
  }

  return (
    <div className="flex rounded border border-disabled divide-x divide-disabled items-stretch">
      {items.map((item) => (
        <div
          key={item.dimensiId}
          className="pl-3 py-5 pr-7 h-auto flex flex-col w-full"
        >
          <Text variant="action" className="mb-1">
            {item.dimensiNama}
          </Text>
          <Text variant="body-sm" color="subdued" className="mb-2">
            {item.dimensiDeskripsi}
          </Text>
          <div className="flex items-center justify-between space-x-2 mt-auto">
            {renderBadge(item.skor)}
            <Text className="italic !text-blue-60" variant="body-sm">
              {item.count > 0 ? `Dipilih ${item.count}` : 'Tidak dipilih'}
            </Text>
          </div>
        </div>
      ))}
    </div>
  )
}

type BannerPenilaianPenetapanSusulanProps = {
  className?: string
}
function BannerPenilaianPenetapanSusulan({
  className,
}: BannerPenilaianPenetapanSusulanProps) {
  const [isOpen, setOpen] = useState(false)

  return (
    <>
      <Banner
        className={clsx('!bg-transparent', className)}
        animationDuration={300}
        title="Lakukan Penilaian dan Penetapan Susulan"
        type="informational"
        closable={false}
      >
        <BannerDescription>
          Tekan “Lakukan Penilaian” untuk menyelesaikan penilaian dan penetapan
          untuk pegawai baru atau guru yang belum selesai dinilai. Cek cara
          selengkapnya.
        </BannerDescription>
        <BannerAction>
          <Button
            variant="solid"
            size="sm"
            color="white"
            onClick={() => setOpen(true)}
          >
            Cek caranya
          </Button>
        </BannerAction>
      </Banner>

      <Dialog isOpen={isOpen} size="md">
        <DialogOverlay />
        <DialogContent>
          <DialogHeader title="Cara melakukan Penilaian dan Penetapan Susulan" />
          <div className="p-6 space-y-4">
            <section className="flex space-x-3">
              <Text
                as="span"
                variant="body-sm"
                className="w-6 h-6 bg-gray-80 rounded-full flex items-center justify-center flex-shrink-0"
                color="inverse"
              >
                1
              </Text>
              <div className="space-y-1">
                <Text as="h3" variant="action">
                  Tekan Lakukan Penilaian dan cek Daftar Penilaian Kinerja
                </Text>
                <Text color="subdued">
                  Pegawai baru dan guru yang belum dinilai akan otomatis muncul
                  di daftar.
                </Text>
                <img
                  className="w-full"
                  width="944"
                  height="102"
                  src={assetUrl(
                    '/images/pengelolaan-kinerja/banner-penilaian.png'
                  )}
                  alt="Banner Penilaian Kinerja. Terdapat tombol Lakukan Penilaian."
                />
                <img
                  className="w-full"
                  width="1258"
                  height="56"
                  src={assetUrl(
                    '/images/pengelolaan-kinerja/table-row-belum-dinilai.png'
                  )}
                  alt="Baris tabel Penilaian Kinerja. Terdapat label belum dinilai."
                />
              </div>
            </section>

            <section className="flex space-x-3">
              <Text
                as="span"
                variant="body-sm"
                className="w-6 h-6 bg-gray-80 rounded-full flex items-center justify-center flex-shrink-0"
                color="inverse"
              >
                2
              </Text>
              <div className="space-y-1">
                <Text as="h3" variant="action">
                  Setelah Penilaian selesai, lakukan Penetapan Predikat Kinerja
                </Text>
                <Text color="subdued">
                  Jika ada penilaian susulan yang sudah selesai, Anda dapat
                  lanjut melakukan penetapan susulan.
                </Text>
                <img
                  className="w-full"
                  width="944"
                  height="102"
                  src={assetUrl(
                    '/images/pengelolaan-kinerja/banner-penetapan-predikat-kinerja-susulan.png'
                  )}
                  alt="Banner Penetapan Predikat Kinerja untuk pegawai baru. Terdapat tombol Lakukan Penetapan."
                />
              </div>
            </section>

            <Button
              size="lg"
              className="mx-auto"
              onClick={() => setOpen(false)}
            >
              Saya mengerti
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

type BannerPenetapanPredikatPegawaiSusulanProps = {
  className?: string
  npsn: string
}
function BannerPenetapanPredikatPegawaiSusulan({
  className,
  npsn,
}: BannerPenetapanPredikatPegawaiSusulanProps) {
  return (
    <div
      className={clsx(
        'bg-white space-x-6 p-6 rounded-lg flex items-center justify-between',
        className
      )}
    >
      <div className="flex items-center space-x-6">
        <img
          src={assetUrl('/illustrations/img-file-document-edit.png')}
          width="84"
          height="84"
          alt=""
        />

        <div className="space-y-2">
          <Text variant="heading-md">
            Lakukan Penetapan Predikat Kinerja Susulan
          </Text>
          <Text color="subdued" variant="eyebrow-lg">
            Pegawai yang belum ditetapkan Predikat Kinerjanya otomatis muncul di
            daftar.
          </Text>
        </div>
      </div>

      <Link
        href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru/penetapan-predikat-kinerja/susulan`}
        passHref
      >
        <Button size="lg" className="flex-shrink-0" as="a" type={undefined}>
          Lakukan Penetapan Susulan
        </Button>
      </Link>
    </div>
  )
}

function BannerEvaluationSent({ isYear2025OrLater = false } = {}) {
  return (
    <div className="mb-6 bg-white rounded">
      <div className="p-6 flex space-x-6 items-center">
        <img
          height={84}
          width={84}
          src={assetUrl('/illustrations/img-thumbs-up.png')}
          className="flex-shrink-0"
          alt=""
        />
        <div className="items-center space-y-2 flex-1">
          <Text variant="heading-md">
            {isYear2025OrLater ? (
              <>Hasil Penilaian Kinerja berhasil dikirim!</>
            ) : (
              <>
                Hasil Penilaian Kinerja Guru bulan{' '}
                <CardPeriodType withYear={false} /> sudah dikirim
              </>
            )}
          </Text>
          <Text variant="eyebrow-lg" color="subdued">
            {isYear2025OrLater
              ? 'Pegawai akan menerima dokumen. Pengelolaan Kinerja Anda sebagai atasan dinyatakan sudah selesai.'
              : 'Pengelolaan Kinerja Anda sebagai atasan sudah selesai.'}
          </Text>
        </div>
      </div>
    </div>
  )
}

function BannerWaitingForPKO() {
  return (
    <div className="bg-white rounded-lg mb-6 border border-subdued">
      <div className="flex gap-4 items-center w-full p-6">
        <img
          src={assetUrl('/illustrations/img-time-document.png')}
          width={84}
          height={84}
          alt=""
        />
        <div className="flex flex-col gap-2">
          <Text variant="heading-md">Menunggu Predikat Kinerja Organisasi</Text>
          <Text color="subdued">
            Predikat sedang diproses oleh Kemendikdasmen dan Dinas Pendidikan,
            setelah selesai Anda dapat segera melakukan Penetapan.
          </Text>
        </div>
      </div>
    </div>
  )
}
